/**
 * Loads Maps JavaScript API + Places via @googlemaps/js-api-loader.
 * Key source: (1) REACT_APP_* embedded at build, or (2) /api/config from server — fixes production
 * when Heroku/build omits REACT_APP_GOOGLE_MAPS_API_KEY but the server has it in env.
 */

import { Loader } from '@googlemaps/js-api-loader';
import { getWebApiBaseUrl } from '../config/apiBaseUrl';

let loader: Loader | null = null;
let loadPromise: Promise<void> | null = null;
let lastApiKeyUsed: string | null = null;

/** Single in-flight fetch for runtime key (concurrent LocationAutocomplete instances). */
let runtimeKeyPromise: Promise<string> | null = null;

function placesReady(): boolean {
  return !!(typeof window !== 'undefined' && window.google?.maps?.places);
}

export function getGoogleMapsApiKey(): string {
  return (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_KEY || '').trim();
}

async function resolveMapsApiKey(): Promise<string> {
  const fromBuild = getGoogleMapsApiKey();
  if (fromBuild) return fromBuild;
  if (typeof window === 'undefined') return '';

  if (!runtimeKeyPromise) {
    runtimeKeyPromise = (async () => {
      try {
        const base = getWebApiBaseUrl().replace(/\/$/, '');
        const res = await fetch(`${base}/config`, { credentials: 'same-origin' });
        if (!res.ok) return '';
        const data = await res.json();
        return (data.googleMapsApiKey || '').trim();
      } catch {
        return '';
      }
    })();
  }
  return runtimeKeyPromise;
}

export function loadGoogleMapsPlaces(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('No window'));
  }
  if (placesReady() && loadPromise) {
    return loadPromise;
  }

  return (async () => {
    const apiKey = await resolveMapsApiKey();
    if (!apiKey) {
      throw new Error(
        'Google Maps API key missing. For production: set REACT_APP_GOOGLE_MAPS_API_KEY on the server ' +
          '(Heroku config / hosting env) so /api/config can return it, or bake it into the client build. ' +
          'Also add your live domain(s) to the key HTTP referrer list in Google Cloud Console.'
      );
    }

    if (lastApiKeyUsed !== null && lastApiKeyUsed !== apiKey) {
      loadPromise = null;
      loader = null;
      const existing = document.getElementById('__googleMapsScriptId');
      existing?.parentNode?.removeChild(existing);
    }
    lastApiKeyUsed = apiKey;

    if (!loader) {
      loader = new Loader({
        apiKey,
        version: 'weekly',
        libraries: ['places'],
        id: '__googleMapsScriptId',
        region: 'US',
        language: 'en',
      });
    }

    if (loadPromise) {
      return loadPromise;
    }

    loadPromise = loader
      .load()
      .then(() => undefined)
      .catch((e: unknown) => {
        loadPromise = null;
        throw e instanceof Error ? e : new Error(String(e));
      });

    return loadPromise;
  })();
}
